import { useEffect, useRef } from 'react';
import { useRouter } from 'next/router';

import ReactHotToast from 'react-hot-toast';

import { commonToast, Options as ToastOptions } from '~utils/commonToast';
import { CTA_BAR_HEIGHT, SEARCH_HOME_TITLE_HEIGHT } from '~constants/common';

import { useDetermineMobile } from '@myrealtrip/device-detect';

const TOP_TOAST_GAP = 8;
const BOTTOM_TOAST_GAP = 20;
const GNB_HEIGHT = 72;

const TOP_POSITIONS: ToastOptions['position'][] = ['top-left', 'top-center', 'top-right'];

interface GetToastVerticalOffsetParams {
  isMobile: boolean;
  position: ToastOptions['position'];
}

function getToastVerticalOffset({ isMobile, position }: GetToastVerticalOffsetParams) {
  const isTopPosition = TOP_POSITIONS.includes(position);

  if (isTopPosition) {
    return isMobile ? SEARCH_HOME_TITLE_HEIGHT + TOP_TOAST_GAP : GNB_HEIGHT;
  }

  return isMobile ? CTA_BAR_HEIGHT + BOTTOM_TOAST_GAP : BOTTOM_TOAST_GAP;
}

type ToastType = keyof typeof commonToast;

interface Params {
  position?: ToastOptions['position'];
  verticalOffset?: ToastOptions['verticalOffset'];
}

const useToast = ({ position = 'top-center', verticalOffset }: Params = {}) => {
  const isMobile = useDetermineMobile();
  const router = useRouter();
  const lastToastId = useRef<string | null>(null);
  const defaultVerticalOffset = verticalOffset ?? getToastVerticalOffset({ isMobile, position });

  const clear = () => {
    ReactHotToast.remove();
  };

  const dismissToastIfDifferentId = (currentId: string) => {
    if (lastToastId.current !== null && lastToastId.current !== currentId) {
      ReactHotToast.dismiss(lastToastId.current);
    }
  };

  const createToastMethod = (type: ToastType) => (options: ToastOptions) => {
    if (options.id) {
      dismissToastIfDifferentId(options.id);
      lastToastId.current = options.id;
    }

    commonToast[type]({
      ...options,
      position: options.position ?? position,
      verticalOffset: options.verticalOffset ?? defaultVerticalOffset,
      titleLineClamp: options.titleLineClamp,
    });
  };

  useEffect(() => {
    const handleRouteChange = () => {
      clear();
    };

    router.events.on('routeChangeStart', handleRouteChange);

    return () => {
      router.events.off('routeChangeStart', handleRouteChange);
    };
  }, [router]);

  return {
    success: createToastMethod('success'),
    error: createToastMethod('error'),
    warning: createToastMethod('warning'),
    neutral: createToastMethod('neutral'),
    blank: createToastMethod('blank'),
    clear,
  };
};

export default useToast;
